import { DegradedBanner } from "../app/DegradedBanner";
import { usePersistentFlag, usePersistentString } from "../app/persistentUi";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { setTheme, useThemeChoice, type ThemeChoice } from "../app/theme";
import { useStreamContext } from "../app/StreamProvider";
import { MAP_SETTING, type VehicleShape } from "./mapSettings";

/**
 * Écran « Paramètres » (backlog §14, P2-41→45). Tout est local au navigateur
 * (`localStorage`) — aucune préférence n'est envoyée au serveur.
 */
const THEMES: { id: ThemeChoice; label: string }[] = [
  { id: "light", label: "Clair" },
  { id: "dark", label: "Sombre" },
  { id: "rose-astuce", label: "Rose Astuce" },
  { id: "rouge-transdev", label: "Rouge Transdev" },
  { id: "system", label: "Système" },
];

export function SettingsScreen() {
  const { status } = useStreamContext();
  const theme = useThemeChoice();

  return (
    <div className="mae-app">
      <Topbar
        current="settings"
        right={
          <>
            <StreamBadge status={status} />
            <TopbarClock />
          </>
        }
      />
      <DegradedBanner />

      <div className="mae-fill mae-scroll-y" style={{ padding: "22px 26px", background: "var(--sentinelle-surface)", maxWidth: 720 }}>
        <section style={{ marginBottom: 28 }}>
          <h2 className="ovl" style={{ marginBottom: 10 }}>
            Thème
          </h2>
          <div className="buttons has-addons" style={{ margin: 0, flexWrap: "wrap" }}>
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`button is-small ${t.id === theme ? "is-primary" : ""}`}
                style={
                  t.id === theme
                    ? { background: "var(--sentinelle-rail)", borderColor: "var(--sentinelle-rail)", color: "#fff" }
                    : undefined
                }
                aria-pressed={t.id === theme}
                onClick={() => setTheme(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="has-text-grey is-size-7" style={{ marginTop: 6 }}>
            « Système » suit le réglage clair / sombre du poste.
          </p>
        </section>

        <section>
          <h2 className="ovl" style={{ marginBottom: 10 }}>
            Cartographie
          </h2>
          <div style={{ display: "grid", gap: 12 }}>
            <SettingToggle
              settingKey={MAP_SETTING.showDisconnected}
              defaultOn
              label="Afficher les véhicules déconnectés"
              hint="Un véhicule qui cesse de remonter reste affiché en grisé à sa dernière position connue."
            />
            <SettingToggle
              settingKey={MAP_SETTING.hideEmptyLines}
              defaultOn
              label="Masquer les lignes sans véhicule"
              hint="N'affiche pas une ligne (ni ses arrêts) si aucun véhicule n'y circule actuellement."
            />
            <SettingToggle
              settingKey={MAP_SETTING.showShapes}
              defaultOn
              label="Afficher les tracés de lignes"
            />
            <SettingToggle
              settingKey={MAP_SETTING.showStops}
              defaultOn
              label="Afficher les arrêts sur la carte"
            />
            <VehicleShapeSetting />
          </div>
        </section>
      </div>
    </div>
  );
}

const VEHICLE_SHAPES: { id: VehicleShape; label: string; hint: string }[] = [
  { id: "arrow", label: "Flèche", hint: "Flèche de cap + pastille code ligne." },
  { id: "rocket", label: "Rectangle directionnel", hint: "Rectangle avec une pointe orientée dans le sens de marche." },
];

function VehicleShapeSetting() {
  const [raw, setRaw] = usePersistentString(MAP_SETTING.vehicleShape);
  const current: VehicleShape = raw === "rocket" ? "rocket" : "arrow";
  const hint = VEHICLE_SHAPES.find((s) => s.id === current)?.hint;
  return (
    <div>
      <span className="is-size-7" style={{ fontWeight: 600, color: "var(--sentinelle-ink)", display: "block", marginBottom: 4 }}>
        Forme du marqueur véhicule
      </span>
      <div className="buttons has-addons" style={{ margin: 0, flexWrap: "wrap" }}>
        {VEHICLE_SHAPES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`button is-small ${s.id === current ? "is-primary" : ""}`}
            style={
              s.id === current
                ? { background: "var(--sentinelle-rail)", borderColor: "var(--sentinelle-rail)", color: "#fff" }
                : undefined
            }
            aria-pressed={s.id === current}
            onClick={() => setRaw(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {hint ? (
        <span className="has-text-grey is-size-7" style={{ display: "block", marginTop: 4 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function SettingToggle({
  settingKey,
  defaultOn,
  label,
  hint,
}: {
  settingKey: string;
  defaultOn: boolean;
  label: string;
  hint?: string;
}) {
  const [on, setOn] = usePersistentFlag(settingKey, defaultOn);
  return (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
      <input type="checkbox" role="switch" checked={on} onChange={(e) => setOn(e.target.checked)} style={{ marginTop: 3 }} />
      <span>
        <span className="is-size-7" style={{ fontWeight: 600, color: "var(--sentinelle-ink)" }}>
          {label}
        </span>
        {hint ? (
          <span className="has-text-grey is-size-7" style={{ display: "block" }}>
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}
